'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { cn } from '@/lib/utils'

import type { StickyTabsType } from '@/types/contentful/StickyTabsType'

export const StickyTabs = ({ data }: { data: StickyTabsType }) => {
  const pathname = usePathname()
  const links = data?.fields?.links

  const calculateAndSetPosition = useDebouncedCallback(() => {
    let topPosition = 0

    const header = document.querySelector('[data-name="header"]')
    if (header) {
      topPosition += (header as HTMLElement).offsetHeight
    }

    const stickyTabs = document.querySelector('[data-name="sticky-tabs"]') as HTMLElement
    if (stickyTabs) {
      stickyTabs.style.top = `${topPosition}px`
    }
  }, 150)

  useEffect(() => {
    // Initial calculation (call the debounced function immediately)
    calculateAndSetPosition()

    // Add event listeners
    window.addEventListener('resize', calculateAndSetPosition)

    // Cleanup function
    return () => {
      window.removeEventListener('resize', calculateAndSetPosition)
    }
  }, [calculateAndSetPosition])

  if (!links?.length) return null

  return (
    <section data-name="sticky-tabs" className="sticky z-1 w-full">
      {
        <ul className="flex items-center justify-start overflow-scroll bg-white md:overflow-auto xl:justify-center">
          {links.map(({ fields, sys }) => {
            const { url, label } = fields || {}

            return (
              <li key={sys?.id || url} className={cn('flex')}>
                <Link
                  key={url}
                  href={url || ''}
                  className={cn(
                    'flex h-14 items-center justify-center border-b-[3px] border-transparent px-6 text-grey-500 hover:text-primary-500',
                    pathname == url && 'border-primary-500 text-primary-500',
                  )}
                >
                  <p className={cn('whitespace-nowrap text-base font-medium leading-6')}>{label}</p>
                </Link>
              </li>
            )
          })}
        </ul>
      }
    </section>
  )
}
